import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { useAuthStore } from '@/stores/auth.store'
import { SupportConfig, Toggle } from './shared'

export const SupportSettings: React.FC = () => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { tenant } = useAuthStore()
  const [form, setForm] = useState<SupportConfig | null>(null)
  const [categoriesText, setCategoriesText] = useState('')
  const [copied, setCopied] = useState(false)
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)

  const { data: config, isLoading } = useQuery({
    queryKey: ['support-module-config'],
    queryFn: async () => {
      const response = await api.get('/support/config')
      return response.data.data as SupportConfig
    },
  })

  useEffect(() => {
    if (config && !form) {
      setForm(config)
      setCategoriesText((config.ticketForm?.categories || []).join(', '))
    }
  }, [config, form])

  const saveMutation = useMutation({
    mutationFn: async (data: Partial<SupportConfig>) => {
      const response = await api.put('/support/config', data)
      return response.data
    },
    onSuccess: () => {
      setMessage({ type: 'success', text: t('supportModule.saveSuccess') })
      queryClient.invalidateQueries({ queryKey: ['support-module-config'] })
      setTimeout(() => setMessage(null), 5000)
    },
    onError: () => {
      setMessage({ type: 'error', text: t('supportModule.saveError') })
      setTimeout(() => setMessage(null), 5000)
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!form) return
    saveMutation.mutate({
      ...form,
      ticketForm: {
        ...form.ticketForm,
        categories: categoriesText.split(',').map(s => s.trim()).filter(Boolean),
      },
      customerAreaVars: form.customerAreaVars,
    })
  }

  const inputClass = 'w-full px-4 py-2 bg-[#1a1a25] border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#006cd4]'
  const selectClass = 'w-full px-4 py-2 bg-[#1a1a25] border border-white/10 rounded-lg text-white focus:outline-none focus:border-[#006cd4]'

  type PlatformPreset = 'php' | 'liquid' | 'wordpress' | 'javascript' | 'custom'

  const platformPresets: Record<PlatformPreset, { userId: string; name: string; email: string }> = {
    php: {
      userId: "<?php echo $user->id; ?>",
      name: "<?php echo $user->name; ?>",
      email: "<?php echo $user->email; ?>",
    },
    liquid: {
      userId: "{{ customer.id }}",
      name: "{{ customer.name }}",
      email: "{{ customer.email }}",
    },
    wordpress: {
      userId: "<?php echo get_current_user_id(); ?>",
      name: "<?php echo wp_get_current_user()->display_name; ?>",
      email: "<?php echo wp_get_current_user()->user_email; ?>",
    },
    javascript: {
      userId: "window.currentUser?.id",
      name: "window.currentUser?.name",
      email: "window.currentUser?.email",
    },
    custom: {
      userId: form?.customerAreaVars?.userId || 'USER_ID_IN_YOUR_SYSTEM',
      name: form?.customerAreaVars?.name || 'CUSTOMER_NAME',
      email: form?.customerAreaVars?.email || 'CUSTOMER_EMAIL',
    },
  }

  const setPlatform = (platform: PlatformPreset) => {
    if (!form) return
    const preset = platformPresets[platform]
    setForm({
      ...form,
      customerAreaVars: {
        platform,
        userId: preset.userId,
        name: preset.name,
        email: preset.email,
      },
    })
  }

  const updateCustomerAreaVar = (field: 'userId' | 'name' | 'email', value: string) => {
    if (!form) return
    setForm({
      ...form,
      customerAreaVars: {
        platform: 'custom' as PlatformPreset,
        userId: vars.userId,
        name: vars.name,
        email: vars.email,
        ...form.customerAreaVars,
        [field]: value,
      },
    })
  }
  const dayNames: string[] = t('supportModule.dayNames', { returnObjects: true }) as string[]

  if (isLoading || !form) {
    return <div className="p-8 text-center text-gray-400">{t('supportModule.loading')}</div>
  }

  const toggleDay = (day: number) => {
    const days = form.humanHours.days.includes(day)
      ? form.humanHours.days.filter(d => d !== day)
      : [...form.humanHours.days, day].sort()
    setForm({ ...form, humanHours: { ...form.humanHours, days } })
  }

  const vars = form.customerAreaVars || { platform: 'custom', userId: 'USER_ID_IN_YOUR_SYSTEM', name: 'CUSTOMER_NAME', email: 'CUSTOMER_EMAIL' }
  const quote = (value: string) => {
    // If the value already looks like a code expression (contains <?php, {{, or starts with window.),
    // leave it unquoted so the backend template can render it. Otherwise wrap in quotes.
    if (/^<\?php|^{{|^window\./.test(value.trim())) return value.trim()
    return `'${value.replace(/'/g, "\\'")}'`
  }

  // Embed snippet for the tenant's customer area: same script, forced
  // support mode + identify() of the logged-in customer
  const clientAreaSnippet = `<script>
  (function(w,d,s,o,f,js,fjs){
    w[o]=w[o]||function(){(w[o].q=w[o].q||[]).push(Array.prototype.slice.call(arguments))};
    js=d.createElement(s),fjs=d.getElementsByTagName(s)[0];
    js.id=o;js.src=f;js.async=1;fjs.parentNode.insertBefore(js,fjs);
  }(window,document,'script','r3Converse','https://cdn.r3Converse.com/widget.js?v=` + __WIDGET_VERSION__ + `'));

  function tryInit(attempts) {
    if (typeof r3Converse === 'function') {
      r3Converse('init', {
        tenantId: '${tenant?.id || 'your-tenant-id'}',
        mode: 'support'
      });
      // Identify the logged-in customer with variables from your backend
      r3Converse('identify', {
        externalId: ${quote(vars.userId)},
        name: ${quote(vars.name)},
        email: ${quote(vars.email)}
      });
    } else if (attempts > 0) {
      setTimeout(function() { tryInit(attempts - 1); }, 500);
    }
  }
  tryInit(10);
</script>`

  const copySnippet = () => {
    navigator.clipboard.writeText(clientAreaSnippet)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <div className="max-w-3xl">
      {message && (
        <div className={`mb-6 p-3 rounded-lg text-sm ${
          message.type === 'success'
            ? 'bg-green-500/10 text-green-400 border border-green-500/20'
            : 'bg-red-500/10 text-red-400 border border-red-500/20'
        }`}>
          {message.text}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* General */}
        <div className="bg-[#12121a] border border-white/10 rounded-xl p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">{t('supportModule.mode')}</label>
            <select
              value={form.mode}
              onChange={(e) => setForm({ ...form, mode: e.target.value as SupportConfig['mode'] })}
              className={inputClass}
            >
              <option value="sales">{t('supportModule.modeSales')}</option>
              <option value="support">{t('supportModule.modeSupport')}</option>
              <option value="both">{t('supportModule.modeBoth')}</option>
            </select>
            <p className="text-xs text-gray-500 mt-1">{t('supportModule.modeDesc')}</p>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-medium">{t('supportModule.enabled')}</h3>
              <p className="text-sm text-gray-500">{t('supportModule.enabledDesc')}</p>
            </div>
            <Toggle checked={form.enabled} onChange={(v) => setForm({ ...form, enabled: v })} label={t('supportModule.enabled')} />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">{t('supportModule.botName')}</label>
            <input
              type="text"
              value={form.botName}
              onChange={(e) => setForm({ ...form, botName: e.target.value })}
              className={inputClass}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">{t('supportModule.greeting')}</label>
            <textarea
              value={form.greeting}
              onChange={(e) => setForm({ ...form, greeting: e.target.value })}
              rows={2}
              className={inputClass}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">{t('supportModule.placeholder')}</label>
            <input
              type="text"
              value={form.placeholder}
              onChange={(e) => setForm({ ...form, placeholder: e.target.value })}
              className={inputClass}
            />
          </div>

          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-medium">{t('supportModule.showTicketButton')}</h3>
              <p className="text-sm text-gray-500">{t('supportModule.showTicketButtonDesc')}</p>
            </div>
            <Toggle checked={form.showTicketButton} onChange={(v) => setForm({ ...form, showTicketButton: v })} label={t('supportModule.showTicketButton')} />
          </div>
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-medium">{t('supportModule.showKbSuggestions')}</h3>
              <p className="text-sm text-gray-500">{t('supportModule.showKbSuggestionsDesc')}</p>
            </div>
            <Toggle checked={form.showKbSuggestions} onChange={(v) => setForm({ ...form, showKbSuggestions: v })} label={t('supportModule.showKbSuggestions')} />
          </div>
        </div>

        {/* Human hours */}
        <div className="bg-[#12121a] border border-white/10 rounded-xl p-6">
          <h3 className="font-medium mb-1">{t('supportModule.humanHours')}</h3>
          <p className="text-sm text-gray-500 mb-4">{t('supportModule.humanHoursDesc')}</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">{t('supportModule.hoursStart')}</label>
              <input
                type="time"
                value={form.humanHours.start}
                onChange={(e) => setForm({ ...form, humanHours: { ...form.humanHours, start: e.target.value } })}
                className={inputClass}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">{t('supportModule.hoursEnd')}</label>
              <input
                type="time"
                value={form.humanHours.end}
                onChange={(e) => setForm({ ...form, humanHours: { ...form.humanHours, end: e.target.value } })}
                className={inputClass}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">{t('supportModule.timezone')}</label>
              <input
                type="text"
                value={form.humanHours.timezone}
                onChange={(e) => setForm({ ...form, humanHours: { ...form.humanHours, timezone: e.target.value } })}
                placeholder="Europe/Madrid"
                className={inputClass}
              />
            </div>
          </div>
          <label className="block text-sm font-medium text-gray-300 mb-2">{t('supportModule.days')}</label>
          <div className="flex flex-wrap gap-2">
            {[1, 2, 3, 4, 5, 6, 0].map((day) => (
              <button
                key={day}
                type="button"
                onClick={() => toggleDay(day)}
                className={`px-3 py-1.5 rounded-lg text-sm transition-colors ${
                  form.humanHours.days.includes(day)
                    ? 'bg-[#006cd4] text-white'
                    : 'bg-[#1a1a25] text-gray-400 hover:bg-white/10'
                }`}
              >
                {dayNames[day]}
              </button>
            ))}
          </div>
        </div>

        {/* Ticket form */}
        <div className="bg-[#12121a] border border-white/10 rounded-xl p-6 space-y-4">
          <div>
            <h3 className="font-medium mb-1">{t('supportModule.ticketForm')}</h3>
            <p className="text-sm text-gray-500">{t('supportModule.ticketFormDesc')}</p>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm">{t('supportModule.askEmail')}</span>
            <Toggle checked={form.ticketForm.askEmail} onChange={(v) => setForm({ ...form, ticketForm: { ...form.ticketForm, askEmail: v } })} label={t('supportModule.askEmail')} />
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm">{t('supportModule.askName')}</span>
            <Toggle checked={form.ticketForm.askName} onChange={(v) => setForm({ ...form, ticketForm: { ...form.ticketForm, askName: v } })} label={t('supportModule.askName')} />
          </div>
          <div className="flex items-center justify-between">
            <span className="text-sm">{t('supportModule.askPhone')}</span>
            <Toggle checked={form.ticketForm.askPhone} onChange={(v) => setForm({ ...form, ticketForm: { ...form.ticketForm, askPhone: v } })} label={t('supportModule.askPhone')} />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">{t('supportModule.formCategories')}</label>
            <input
              type="text"
              value={categoriesText}
              onChange={(e) => setCategoriesText(e.target.value)}
              className={inputClass}
            />
          </div>
        </div>

      {/* Client-area embed snippet */}
      <div className="bg-[#12121a] border border-white/10 rounded-xl p-6 space-y-4">
        <div>
          <h3 className="font-medium mb-1">{t('supportModule.clientAreaSnippet')}</h3>
          <p className="text-sm text-gray-500">{t('supportModule.clientAreaSnippetDesc')}</p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-300 mb-2">{t('supportModule.customerAreaPlatform')}</label>
          <select
            value={vars.platform}
            onChange={(e) => setPlatform(e.target.value as PlatformPreset)}
            className={selectClass}
          >
            <option value="custom">{t('supportModule.platformCustom')}</option>
            <option value="php">{t('supportModule.platformPhp')}</option>
            <option value="liquid">{t('supportModule.platformLiquid')}</option>
            <option value="wordpress">{t('supportModule.platformWordPress')}</option>
            <option value="javascript">{t('supportModule.platformJavaScript')}</option>
          </select>
          <p className="text-xs text-gray-500 mt-1">{t('supportModule.customerAreaPlatformHint')}</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">{t('supportModule.customerAreaUserId')}</label>
            <input
              type="text"
              value={vars.userId}
              onChange={(e) => updateCustomerAreaVar('userId', e.target.value)}
              className={inputClass}
              placeholder="USER_ID_IN_YOUR_SYSTEM"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">{t('supportModule.customerAreaName')}</label>
            <input
              type="text"
              value={vars.name}
              onChange={(e) => updateCustomerAreaVar('name', e.target.value)}
              className={inputClass}
              placeholder="CUSTOMER_NAME"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-2">{t('supportModule.customerAreaEmail')}</label>
            <input
              type="text"
              value={vars.email}
              onChange={(e) => updateCustomerAreaVar('email', e.target.value)}
              className={inputClass}
              placeholder="CUSTOMER_EMAIL"
            />
          </div>
        </div>

        <p className="text-xs text-gray-500">{t('supportModule.customerAreaVarsHint')}</p>

        <div className="relative">
          <pre className="bg-[#0a0a0f] border border-white/10 rounded-lg p-4 text-xs text-gray-300 overflow-x-auto whitespace-pre-wrap break-all">
            <code>{clientAreaSnippet}</code>
          </pre>
          <button
            onClick={copySnippet}
            className="absolute top-2 right-2 px-3 py-1 text-sm bg-[#006cd4]/20 text-[#006cd4] rounded-lg hover:bg-[#006cd4]/30 transition-colors"
          >
            {copied ? t('common.copied') : t('common.copy')}
          </button>
        </div>
      </div>

      <button
        type="submit"
        disabled={saveMutation.isPending}
        className="w-full px-8 py-3 bg-[#006cd4] text-white font-semibold rounded-lg hover:bg-[#0052a3] transition-colors disabled:opacity-50"
      >
        {saveMutation.isPending ? t('common.saving') : t('common.save')}
      </button>
      </form>
    </div>
  )
}
